import { createRoute, redirect } from '@tanstack/react-router'
import { lazy } from 'react'
import type { PlatformTab } from '../../routes/SuperAdminPage'
import { listSearch } from '../list-search'
import type { LazyPage } from './lazy-page'
import { PLATFORM_NAV } from './nav'
import { rootRoute } from './root'

// The section union comes from the page itself. Spelled out here it was a third
// copy of the same list, and the copy is what let a new section typecheck while
// rendering the wrong one — a type-only import erases, so the page stays lazy.
const SuperAdminPage: LazyPage<{ active: PlatformTab; view?: string }> = lazy(
  () =>
    import('../../routes/SuperAdminPage').then((m) => ({
      default: m.SuperAdminPage,
    })),
)
/**
 * The platform surface, its section carried in the path (tasks · config) and a
 * section's own view carried after it (`/platform/type/compare`) — or the
 * record open in it, by its key (`/platform/docs/<key>`).
 *
 * The view is in the path rather than in the page's state because a tab is a
 * PLACE: a comparison worth showing somebody is worth sending them a link to,
 * and a reload should not drop them back on the first view. A record is one
 * for the same reason, and a doc more than most: it is cited by its key.
 */
function PlatformSection({
  section,
  view,
}: {
  section: string
  view?: string
}) {
  /*
   * Validated against the nav rather than against a second list written here.
   * The two had already drifted: a section in PLATFORM_NAV and not in this
   * check rendered the task board underneath the new section's title, which
   * reads as the page being broken rather than as a route being unlisted.
   */
  const known = PLATFORM_NAV.some((n) => n.section === section)
  return (
    <SuperAdminPage
      active={known ? (section as PlatformTab) : 'tasks'}
      view={view}
    />
  )
}

/* Each route reads its OWN params. A hook belongs to the route it was made
   from, so a shared component reaching for another route's params throws the
   moment the other one is what matched. */
function PlatformSectionRoot() {
  const { section } = platformSectionRoute.useParams()
  return <PlatformSection section={section} />
}

/** The same section, reached at one of its views. */
function PlatformSectionView() {
  const { section, view } = platformViewRoute.useParams()
  return <PlatformSection section={section} view={view} />
}
const platformIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform',
  beforeLoad: () => {
    throw redirect({ to: '/platform/$section', params: { section: 'tasks' } })
  },
})
const platformSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/$section',
  component: PlatformSectionRoot,
  validateSearch: listSearch,
})
const platformViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/$section/$view',
  component: PlatformSectionView,
  validateSearch: listSearch,
})

export const platformRoutes = [
  platformIndexRoute,
  platformSectionRoute,
  platformViewRoute,
]
